import classNames from 'classnames';
import arrayMutators from 'final-form-arrays';
import { bool, node, string } from 'prop-types';
import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import ReCAPTCHA from 'react-google-recaptcha';
import PhoneInput from 'react-phone-input-2';
import { compose } from 'redux';
import { CustomExtendedDataField, FieldTextInput, Form, PrimaryButton } from '../../../components';
import useCaptcha from '../../../hooks/useCaptcha/useCaptcha';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';
import * as validators from '../../../util/validators';
import FieldSelectUserType from '../FieldSelectUserType';
import css from './SignupForm.module.css';

// Normalize KSA MSISDN to 966XXXXXXXXX (no leading +). Accepted: 5, 05, 9665, +9665, 009665.
// 05XXXXXXXX → 96605XXXXXXXX (13 digits); 5XXXXXXXX → 9665XXXXXXXX (12 digits).
const normalizeMsisdn = raw => {
  if (!raw) return null;
  let s = String(raw).trim().replace(/\s+/g, '');
  if (s.startsWith('+')) s = s.slice(1);
  if (s.startsWith('00966')) s = s.replace(/^00966/, '966');
  if (s.startsWith('966')) return s;
  if (s.startsWith('05')) return '966' + s.slice(1);
  if (s.startsWith('5')) return '966' + s;
  return s;
};

// Validate KSA mobile. Accept 5XXXXXXXX, 05XXXXXXXX, 9665..., +9665..., 009665...
// 9665XXXXXXXX (12 digits) or 96605XXXXXXXX (13 digits).
const isValidKsaMsisdnInput = rawValue => {
  if (!rawValue) return false;
  const norm = normalizeMsisdn(String(rawValue));
  if (!norm) return false;
  return /^9665\d{8}$/.test(norm) || /^96605\d{8}$/.test(norm);
};



const getSoleUserTypeMaybe = userTypes =>
  Array.isArray(userTypes) && userTypes.length === 1 ? userTypes[0].userType : null;

const SignupFormComponent = props => {
  if (typeof window === 'undefined') return null;
  require('../../../styles/react-phone-input-2.css');


  const { isMerchant } = props;
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const {
    captchaToken,
    isVerified,
    errorMessage,
    onCaptchaChange,
  } = useCaptcha();
  // Toggle password visibility
  const handleTogglePasswordVisibility = () => {
    setShowPassword(prevState => !prevState);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(prevState => !prevState);
  };

  return (
    <FinalForm
      {...props}
      mutators={{ ...arrayMutators }}
      initialValues={{
        userType: props.preselectedUserType || getSoleUserTypeMaybe(props.userTypes),
      }}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          handleSubmit,
          form,
          inProgress,
          invalid,
          intl,
          termsAndConditions,
          preselectedUserType,
          userTypes,
          userFields,
          values,
          submitSucceeded,
        } = formRenderProps;

        const { userType, password, isPasswordMismatch, mobile_phone_number } = values || {};

        const requiresBusinessPhone = isMerchant || userType === 'provider';
        const hasPhone = !!mobile_phone_number?.phoneNumber;
        const isValidPhoneNumber = hasPhone
          ? isValidKsaMsisdnInput(mobile_phone_number?.value)
          : false;

        // Email field validation
        const emailRequired = validators.required(
          intl.formatMessage({ id: 'SignupForm.emailRequired' })
        );
        const emailValid = validators.emailFormatValid(
          intl.formatMessage({ id: 'SignupForm.emailInvalid' })
        );

        const passwordValid = validators.passwordFormatValid(
          intl.formatMessage({ id: 'SignupForm.passwordInvalid' })
        );

        // Password field validation
        const passwordRequiredMessage = intl.formatMessage({ id: 'SignupForm.passwordRequired' });

        const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
        const passwordValidators = validators.composeValidators(passwordRequired, passwordValid);

        // Confirm Password validation
        const confirmPasswordRequiredMessage = intl.formatMessage({
          id: 'SignupForm.confirmPasswordRequired',
        });

        const confirmPasswordRequired = validators.requiredStringNoTrim(
          confirmPasswordRequiredMessage
        );

        const confirmPasswordMatch = value =>
          value === password
            ? undefined
            : intl.formatMessage({ id: 'SignupForm.confirmPasswordMismatch' });

        const confirmPasswordValidators = validators.composeValidators(
          confirmPasswordRequired
        );

        // Date of Birth validation
        const dateOfBirthRequired = validators.required(
          intl.formatMessage({ id: 'SignupForm.dateOfBirthRequired' })
        );

        // Custom user fields - filter to only show gender and dateOfBirth in signup
        const allUserFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);
        const userFieldProps = allUserFieldProps.filter(field =>
          field.key === 'prot_gender' || field.key === 'prot_dateOfBirth'
        );

        // Determine if user type and custom fields should be shown
        const noUserTypes = !userType && !(userTypes?.length > 0);
        const userTypeConfig = userTypes.find(config => config.userType === userType);
        const showDefaultUserFields = userType || noUserTypes;
        const showCustomUserFields = (userType || noUserTypes) && userFieldProps?.length > 0;

        const classes = classNames(rootClassName || css.root, className, isMerchant && css.merchantForm);
        const submitInProgress = inProgress;
        const showPhoneErrors = requiresBusinessPhone && !submitInProgress && !submitSucceeded && phoneTouched;
        const submitDisabled =
          invalid ||
          submitInProgress ||
          isPasswordMismatch ||
          !isVerified ||
          (requiresBusinessPhone && (!hasPhone || !isValidPhoneNumber));


        const handleChangePhoneInput = (value, country) => {
          const { dialCode = '', countryCode = '' } = country;
          const normalizedValue = normalizeMsisdn(value) || value;
          const normalizedWithoutDialCode =
            dialCode && String(normalizedValue).startsWith(String(dialCode))
              ? String(normalizedValue).slice(String(dialCode).length)
              : String(normalizedValue);
          form.change('phoneNumber', '+' + normalizedValue);
          const newState = {
            countryCode: countryCode?.toUpperCase(),
            callingCode: dialCode,
            phoneNumber: normalizedWithoutDialCode,
            value: normalizedValue,
          }
          form.change('mobile_phone_number', newState);
        };

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <FieldSelectUserType
              name="userType"
              userTypes={userTypes}
              hasExistingUserType={!!preselectedUserType}
              intl={intl}
              className={css.inputBox}
            />

            {showDefaultUserFields ? (
              <div className={css.defaultUserFields}>
                {showDefaultUserFields !== 'provider' ? (
                  <FieldTextInput
                    type="email"
                    id={formId ? `${formId}.email` : 'email'}
                    name="email"
                    className={css.inputBox}
                    autoComplete="off"
                    label={intl.formatMessage({ id: 'SignupForm.emailLabel' })}
                    placeholder={intl.formatMessage({ id: 'SignupForm.emailPlaceholder1' })}
                    validate={validators.composeValidators(emailRequired, emailValid)}
                  />
                ) : null}

                {showDefaultUserFields === "provider" ?
                  <FieldTextInput
                    id={`${formId}business_name`}
                    name="business_name"
                    className={css.inputBox}
                    type="text"
                    placeholder={intl.formatMessage({
                      id: 'EditListingBusinessForm.businessNamePlaceholder',
                    })}
                    validate={validators.required(
                      intl.formatMessage({ id: 'EditListingBusinessForm.businessNameRequired' })
                    )}
                  />
                  : null}

                <PhoneInput
                  className={css.phoneInput}
                  name="phoneNumber"
                  id={formId ? `${formId}.phoneNumber` : 'phoneNumber'}
                  country={'sa'}
                  onlyCountries={['sa']}
                  disableDropdown={true}
                  value={mobile_phone_number?.callingCode && mobile_phone_number?.phoneNumber
                    ? '+' + mobile_phone_number.callingCode + mobile_phone_number.phoneNumber
                    : ''}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.phoneNumberLabel',
                  })}
                  onChange={handleChangePhoneInput}
                  onBlur={() => setPhoneTouched(true)}
                />

                {showPhoneErrors && !hasPhone ? (
                  <div className={css.errorText}>
                    <FormattedMessage id="SignupForm.phoneNumberRequired" />
                  </div>
                ) : null}
                {showPhoneErrors && hasPhone && !isValidPhoneNumber ? (
                  <div className={css.errorText}>
                    <FormattedMessage id="EditListingDetailsForm.phoneNumberInvalid" />
                    
                  </div>
                ) : null}

                <div className={classNames(css.name)}>
                  <FieldTextInput
                    className={css.inputBox}
                    type="text"
                    id={formId ? `${formId}.fname` : 'fname'}
                    name="fname"
                    autoComplete="off"
                    label={intl.formatMessage({ id: 'SignupForm.firstNameLabel' })}
                    placeholder={intl.formatMessage({ id: 'SignupForm.firstNamePlaceholder1' })}
                    validate={validators.required(
                      intl.formatMessage({ id: 'SignupForm.firstNameRequired' })
                    )}
                  />
                  <FieldTextInput
                    className={css.inputBox}
                    type="text"
                    id={formId ? `${formId}.lname` : 'lname'}
                    name="lname"
                    autoComplete="off"
                    label={intl.formatMessage({ id: 'SignupForm.lastNameLabel' })}
                    placeholder={intl.formatMessage({ id: 'SignupForm.lastNamePlaceholder1' })}
                    validate={validators.required(
                      intl.formatMessage({ id: 'SignupForm.lastNameRequired' })
                    )}
                  />
                </div>

                {/* Gender and DOB fields for both customers and merchants */}
                {showCustomUserFields ? (
                  <div className={classNames(css.inputBox, css.customUserFields)}>
                    {userFieldProps.map(fieldProps => (
                      <CustomExtendedDataField key={fieldProps.key} {...fieldProps} formId={formId} />
                    ))}
                  </div>
                ) : null}

                {showDefaultUserFields === "provider" ?
                  <FieldTextInput
                    type="email"
                    id={formId ? `${formId}.email` : 'email'}
                    name="email"
                    className={css.inputBox}
                    autoComplete="off"
                    label={intl.formatMessage({ id: 'SignupForm.emailLabel' })}
                    placeholder={intl.formatMessage({ id: 'SignupForm.emailPlaceholder1' })}
                    validate={validators.composeValidators(emailRequired, emailValid)}
                  />
                  : null}


                {/* Password Field */}
                <div className={css.passwordContainer}>
                  <FieldTextInput
                    showPassword={showPassword}
                    handleTogglePasswordVisibility={handleTogglePasswordVisibility}
                    showPass={true}
                    className={css.inputBox}
                    type={showPassword ? 'text' : 'password'}
                    id={formId ? `${formId}.password` : 'password'}
                    name="password"
                    autoComplete="new-password"
                    label={intl.formatMessage({ id: 'SignupForm.passwordLabel' })}
                    placeholder={intl.formatMessage({ id: 'SignupForm.passwordPlaceholder' })}
                    validate={passwordValidators}
                    onChange={e => {
                      const { value } = e.target;
                      form.change('password', value);

                      value !== values?.confirmPassword ?
                        form.change('isPasswordMismatch', true)
                        : form.change('isPasswordMismatch', false);
                    }}
                  />
                </div>

                {/* Confirm Password Field */}
                <div className={css.passwordContainer}>
                  <FieldTextInput
                    showPassword={showConfirmPassword}
                    handleTogglePasswordVisibility={handleToggleConfirmPasswordVisibility}
                    showPass={true}
                    className={css.inputBox}
                    type={showConfirmPassword ? 'text' : 'password'}
                    id={formId ? `${formId}.confirmPassword` : 'confirmPassword'}
                    name="confirmPassword"
                    autoComplete="new-password"
                    label={intl.formatMessage({ id: 'SignupForm.confirmPasswordLabel' })}
                    placeholder={intl.formatMessage({
                      id: 'SignupForm.confirmPasswordPlaceholder',
                    })}
                    validate={confirmPasswordValidators}
                    onChange={e => {
                      const { value } = e.target;
                      form.change('confirmPassword', value);

                      value !== values?.password ?
                        form.change('isPasswordMismatch', true)
                        : form.change('isPasswordMismatch', false);
                    }}
                  />
                </div>

                {values?.confirmPassword && isPasswordMismatch ?
                  <div className={css.errorText}><FormattedMessage id='SignupForm.confirmPasswordMismatch' /></div>
                  : null}

                {/* <UserFieldPhoneNumber
                  formName="SignupForm"
                  className={css.row}
                  userTypeConfig={userTypeConfig}
                  intl={intl}
                /> */}
              </div>
            ) : null}

            <div className={css.bottomWrapper}>
              {termsAndConditions}
              <div className={css.captchaWrapper}>
                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY}
                  onChange={onCaptchaChange} // Update the token when CAPTCHA is completed
                />
              </div>
              <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
                {isMerchant ? <FormattedMessage id="SignupForm.signUpMerchant" /> : <FormattedMessage id="SignupForm.signUp" />}
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

SignupFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  formId: null,
  inProgress: false,
  preselectedUserType: null,
};

SignupFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  formId: string,
  inProgress: bool,
  termsAndConditions: node.isRequired,
  preselectedUserType: string,
  userTypes: propTypes.userTypes.isRequired,
  userFields: propTypes.listingFields.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const SignupForm = compose(injectIntl)(SignupFormComponent);
SignupForm.displayName = 'SignupForm';

export default SignupForm;
